import ReactMarkdown from "react-markdown";
import { Link } from "react-router-dom";
import remarkGfm from "remark-gfm";
import { C } from "@/lib/brand";

// Maps the BE's content-type enum (the same one returned by
// `GET /affiliate/content?contentType=...`) to the affiliate-portal
// route that surfaces it. When a playbook references one of these
// tokens we render it as a brand-styled link-chip instead of a code
// block. Keeps the reader continuous with the rest of the portal.
const CONTENT_TYPE_ROUTES = {
  sample_post: { path: "/affiliates/dashboard/assets/posts", label: "SAMPLE POSTS" },
  faq_response: { path: "/affiliates/dashboard/assets/faqs", label: "FAQS" },
  story_idea: { path: "/affiliates/dashboard/assets/story-ideas", label: "STORY IDEAS" },
  hook: { path: "/affiliates/dashboard/assets/hooks", label: "HOOKS" },
  benefit_card: { path: "/affiliates/dashboard/assets/benefit-cards", label: "BENEFIT CARDS" },
  comparison_table: { path: "/affiliates/dashboard/assets/comparison", label: "COMPARISON" },
  disclaimer: { path: "/affiliates/dashboard/assets/disclaimers", label: "DISCLAIMERS" },
  playbook_section: { path: "/affiliates/dashboard/assets/playbooks", label: "PLAYBOOKS" },
  foundational: { path: "/affiliates/dashboard/assets/compliance", label: "START HERE" },
};

function extractCodeText(children) {
  if (children == null) return "";
  if (typeof children === "string") return children.trim();
  if (Array.isArray(children)) return children.map(extractCodeText).join("").trim();
  if (typeof children === "object" && children.props) {
    return extractCodeText(children.props.children);
  }
  return "";
}

// Renders a single content-type token as a brand-styled link chip with
// a → arrow. The optional `programSlug` lets us pre-filter the
// destination to the right program when the affiliate clicks through.
function ContentLinkChip({ token, programSlug, block }) {
  const meta = CONTENT_TYPE_ROUTES[token];
  if (!meta) return null;
  const href = programSlug ? `${meta.path}?program=${encodeURIComponent(programSlug)}` : meta.path;
  return (
    <Link
      to={href}
      data-testid={`content-link-${token}`}
      style={{
        display: block ? "flex" : "inline-flex",
        alignItems: "center",
        gap: 10,
        padding: block ? "14px 18px" : "4px 10px",
        background: C.ink,
        color: C.cream,
        textDecoration: "none",
        fontSize: block ? 13 : 11,
        fontWeight: 800,
        letterSpacing: "0.18em",
        marginBottom: block ? 18 : 0,
        marginRight: block ? 0 : 4,
        transition: "background 140ms",
      }}
      onMouseEnter={(e) => { e.currentTarget.style.background = C.peachDeep; }}
      onMouseLeave={(e) => { e.currentTarget.style.background = C.ink; }}
    >
      <span style={{ fontFamily: "JetBrains Mono, monospace", letterSpacing: 0, textTransform: "none" }}>
        {token}
      </span>
      <span style={{ opacity: 0.6 }}>·</span>
      <span>{meta.label}</span>
      <span style={{ marginLeft: 4 }}>→</span>
    </Link>
  );
}

// Brand-styled markdown renderer for affiliate content (compliance,
// playbooks, etc). Keeps prose comfortable to read on mobile by
// constraining measure to ~70ch and using the brand's serif for body.
// All link clicks open in a new tab so the affiliate doesn't lose
// their dashboard context.
//
// When the source references the BE's content-type enum (e.g.
// `sample_post`, `faq_response`), we replace the rendered code block /
// inline code with a branded link chip that deep-links to the matching
// asset surface. `programSlug` (optional) flows through as a query
// param so the destination can pre-filter.
export default function MarkdownReader({ source, testId, programSlug }) {
  return (
    <article
      data-testid={testId || "markdown-reader"}
      className="voya9-md"
      style={{
        maxWidth: "72ch",
        fontSize: 16,
        lineHeight: 1.7,
        color: C.ink,
      }}
    >
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        components={{
          h1: ({ node, ...p }) => (
            <h1 style={{ fontSize: 36, fontWeight: 800, lineHeight: 1.1, letterSpacing: "-0.02em", marginTop: 8, marginBottom: 20, color: C.ink }} {...p} />
          ),
          h2: ({ node, children, ...p }) => {
            const text = Array.isArray(children)
              ? children.map((c) => (typeof c === "string" ? c : c?.props?.children || "")).join("")
              : String(children || "");
            const id = text
              .toLowerCase()
              .replace(/[^a-z0-9\s-]/g, "")
              .replace(/\s+/g, "-")
              .replace(/-+/g, "-")
              .replace(/^-|-$/g, "");
            return (
              <h2
                id={id}
                style={{ fontSize: 24, fontWeight: 800, lineHeight: 1.2, letterSpacing: "-0.01em", marginTop: 40, marginBottom: 14, color: C.ink, borderTop: `1px solid ${C.warmGray3}`, paddingTop: 32, scrollMarginTop: 120 }}
                {...p}
              >
                {children}
              </h2>
            );
          },
          h3: ({ node, ...p }) => (
            <h3 style={{ fontSize: 18, fontWeight: 800, marginTop: 28, marginBottom: 8, color: C.ink, textTransform: "uppercase", letterSpacing: "0.04em" }} {...p} />
          ),
          h4: ({ node, ...p }) => (
            <h4 style={{ fontSize: 15, fontWeight: 800, marginTop: 22, marginBottom: 6, color: C.ink, fontFamily: '"Instrument Serif", Georgia, serif', fontStyle: "italic" }} {...p} />
          ),
          p: ({ node, ...p }) => (
            <p style={{ marginTop: 0, marginBottom: 16, color: C.ink, lineHeight: 1.7 }} {...p} />
          ),
          ul: ({ node, ordered, ...p }) => (
            <ul style={{ paddingLeft: 22, marginTop: 0, marginBottom: 18 }} {...p} />
          ),
          ol: ({ node, ordered, ...p }) => (
            <ol style={{ paddingLeft: 22, marginTop: 0, marginBottom: 18 }} {...p} />
          ),
          li: ({ node, ordered, checked, ...p }) => (
            <li style={{ marginBottom: 8, lineHeight: 1.65 }} {...p} />
          ),
          blockquote: ({ node, ...p }) => (
            <blockquote
              style={{
                borderLeft: `3px solid ${C.peachDeep}`,
                background: C.bone,
                padding: "14px 18px",
                margin: "18px 0",
                fontFamily: '"Instrument Serif", Georgia, serif',
                fontStyle: "italic",
                fontSize: 17,
                color: C.ink,
              }}
              {...p}
            />
          ),
          a: ({ node, ...p }) => (
            <a
              {...p}
              target="_blank"
              rel="noopener noreferrer"
              style={{ color: C.peachDeep, textDecoration: "underline", textUnderlineOffset: 3 }}
            />
          ),
          code: ({ node, inline, children, ...p }) => {
            const text = extractCodeText(children);
            // Tokenize: if the code block/inline contains a known
            // content-type slug, render the link chip instead.
            if (CONTENT_TYPE_ROUTES[text]) {
              return <ContentLinkChip token={text} programSlug={programSlug} block={!inline} />;
            }
            return inline ? (
              <code
                style={{
                  background: C.bone,
                  padding: "2px 6px",
                  fontSize: 13,
                  fontFamily: "JetBrains Mono, monospace",
                  color: C.ink,
                }}
                {...p}
              >
                {children}
              </code>
            ) : (
              <code
                style={{
                  display: "block",
                  background: C.ink,
                  color: C.cream,
                  padding: 16,
                  fontSize: 13,
                  fontFamily: "JetBrains Mono, monospace",
                  overflowX: "auto",
                  marginBottom: 18,
                  whiteSpace: "pre-wrap",
                  lineHeight: 1.5,
                }}
                {...p}
              >
                {children}
              </code>
            );
          },
          pre: ({ node, ...p }) => <pre style={{ margin: 0, padding: 0 }} {...p} />,
          hr: () => (
            <hr style={{ border: 0, borderTop: `1px dashed ${C.warmGray3}`, margin: "28px 0" }} />
          ),
          table: ({ node, ...p }) => (
            <div style={{ overflowX: "auto", marginBottom: 18 }}>
              <table
                style={{
                  borderCollapse: "collapse",
                  width: "100%",
                  fontSize: 14,
                }}
                {...p}
              />
            </div>
          ),
          th: ({ node, ...p }) => (
            <th
              style={{
                textAlign: "left",
                padding: "10px 12px",
                background: C.bone,
                fontSize: 11,
                fontWeight: 800,
                letterSpacing: "0.1em",
                textTransform: "uppercase",
                color: C.ink,
                borderBottom: `1.5px solid ${C.ink}`,
              }}
              {...p}
            />
          ),
          td: ({ node, ...p }) => (
            <td
              style={{
                padding: "10px 12px",
                borderBottom: `1px solid ${C.warmGray3}`,
                verticalAlign: "top",
              }}
              {...p}
            />
          ),
          strong: ({ node, ...p }) => <strong style={{ fontWeight: 800, color: C.ink }} {...p} />,
          em: ({ node, ...p }) => (
            <em style={{ fontFamily: '"Instrument Serif", Georgia, serif', fontStyle: "italic" }} {...p} />
          ),
        }}
      >
        {source || ""}
      </ReactMarkdown>
    </article>
  );
}
